import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { useTable } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import type { DashboardLendPositionsFilters as DashboardFilters } from '@/helpers/filter/table';
import type { UserLendPositionsOrderDirection } from '@/hooks/positions/userLendPositionsQuery';
import useTableSettings from '@/helpers/cookieStorage/userSettings/tableSettings/client/useTableSettings';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { LEND_POSITIONS_DEFAULT_ORDER_BY, useLendPositionsTableData } from '@/hooks/positions/useLendPositionsTableData';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import { useTableFiltersHandler } from '../../hooks/useTableFiltersHandler';
import { COLUMN_ID_TO_ORDER_BY, LEND_POSITIONS_DEFAULT_TABLE_SETTINGS, LEND_POSITIONS_STORAGE_KEY } from '../config/config';
import { columnLabelsLendPositions, columnsLendPositions, lendPositionsTableFeatures } from '../config/LendPositionsTable.columns';
import { useDashboardLendPositionsFilters } from './useDashboardLendPositionsFilters';
import { useLendPositionsTableRows } from './useLendPositionsTableRows';
export const useDashboardLendPositionsTable = () => {
  const $ = _c(39);
  const {
    address: accountAddress
  } = useViewedAccountAddress();
  let t0;
  if ($[0] !== accountAddress) {
    t0 = {
      pageKey: PageKey.Dashboard,
      tableSettingsObjectKey: LEND_POSITIONS_STORAGE_KEY,
      defaultTableSettings: LEND_POSITIONS_DEFAULT_TABLE_SETTINGS,
      resetFiltersKey: accountAddress
    };
    $[0] = accountAddress;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters
  } = useTableSettings(t0);
  const sortingItem = sorting[0];
  const orderBy = COLUMN_ID_TO_ORDER_BY[sortingItem?.id as string] ?? LEND_POSITIONS_DEFAULT_ORDER_BY;
  const orderDirection = sortingItem?.desc === false ? "asc" : "desc";
  let t1;
  if ($[2] !== accountAddress || $[3] !== orderBy || $[4] !== orderDirection) {
    t1 = {
      accountAddress,
      orderBy,
      orderDirection
    };
    $[2] = accountAddress;
    $[3] = orderBy;
    $[4] = orderDirection;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    lendPositions,
    facets,
    partial,
    loading,
    error,
    isNotFound,
    isPlaceholderData
  } = useLendPositionsTableData(t1);
  const [settledAddress, setSettledAddress] = useState(undefined);
  let t2;
  let t3;
  if ($[6] !== accountAddress || $[7] !== isPlaceholderData) {
    t2 = () => {
      if (!isPlaceholderData) {
        setSettledAddress(accountAddress);
      }
    };
    t3 = [isPlaceholderData, accountAddress];
    $[6] = accountAddress;
    $[7] = isPlaceholderData;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  const isStaleAccountData = isPlaceholderData && settledAddress !== accountAddress;
  let t4;
  if ($[10] !== lendPositions) {
    t4 = {
      lendPositions
    };
    $[10] = lendPositions;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const lendPositionsRows = useLendPositionsTableRows(t4);
  let t5;
  if ($[12] !== columnFilters || $[13] !== lendPositionsRows) {
    t5 = {
      items: lendPositionsRows,
      columnFilters
    };
    $[12] = columnFilters;
    $[13] = lendPositionsRows;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    filters,
    filteredItems
  } = useDashboardLendPositionsFilters(t5);
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      collateralAssetIdsFilter: "collateralAssetIds",
      loanAssetIdsFilter: "loanAssetIds",
      chainIdsFilter: "chainIds"
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== setColumnFilters) {
    t7 = {
      setColumnFilters,
      filterMapping: t6
    };
    $[16] = setColumnFilters;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const {
    handleFiltersChange
  } = useTableFiltersHandler(t7);
  let t8;
  if ($[18] !== facets?.chainIds) {
    t8 = facets?.chainIds.map(_temp);
    $[18] = facets?.chainIds;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const positionChainIds = t8;
  let t9;
  if ($[20] !== columnFilters || $[21] !== sorting) {
    t9 = {
      sorting,
      columnFilters
    };
    $[20] = columnFilters;
    $[21] = sorting;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== filteredItems || $[24] !== setSorting || $[25] !== t9) {
    t10 = {
      features: lendPositionsTableFeatures,
      data: filteredItems,
      columns: columnsLendPositions,
      onSortingChange: setSorting,
      enableSortingRemoval: false,
      sortDescFirst: true,
      state: t9,
      getRowId: _temp2
    };
    $[23] = filteredItems;
    $[24] = setSorting;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const table = useTable(t10);
  let t11;
  if ($[27] !== error || $[28] !== facets || $[29] !== filters || $[30] !== handleFiltersChange || $[31] !== isNotFound || $[32] !== isStaleAccountData || $[33] !== lendPositionsRows || $[34] !== loading || $[35] !== partial || $[36] !== positionChainIds || $[37] !== table) {
    t11 = {
      lendPositionsRows,
      facets,
      partial,
      loading,
      error,
      isNotFound,
      isStaleAccountData,
      table,
      filters,
      handleFiltersChange,
      positionChainIds,
      columnLabels: columnLabelsLendPositions
    };
    $[27] = error;
    $[28] = facets;
    $[29] = filters;
    $[30] = handleFiltersChange;
    $[31] = isNotFound;
    $[32] = isStaleAccountData;
    $[33] = lendPositionsRows;
    $[34] = loading;
    $[35] = partial;
    $[36] = positionChainIds;
    $[37] = table;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  return t11;
};
function _temp(facet) {
  return facet.value as ChainId;
}
function _temp2(row) {
  return row.id;
}